import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  getDocFromServer,
} from "firebase/firestore";
import firestoreInstance from "../services/firestoreInstance";
import { Participation, ParticipationFirestore, Players } from "../types";

/** Convertit dateInscription Firestore / legacy en Date JS ; null si invalide. */
function toParticipationDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (value instanceof Timestamp) {
    return value.toDate();
  }
  if (typeof value === "object" && typeof (value as { toDate?: unknown }).toDate === "function") {
    try {
      const d = (value as { toDate: () => Date }).toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    } catch {
      return null;
    }
  }
  if (typeof value === "string" || typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/** Charge la map playerId → numéro de dossard pour un tournoi. */
export async function fetchTournamentDossards(
  tournamentId: string
): Promise<Map<string, number>> {
  const map = new Map<string, number>();
  try {
    const snap = await getDocs(
      collection(firestoreInstance, "tournois", tournamentId, "dossards")
    );
    for (const d of snap.docs) {
      const value = d.data()?.dossard;
      if (typeof value === "number") {
        map.set(d.id, value);
      }
    }
  } catch (error) {
    // Ne pas faire échouer toute la liste si les règles dossards sont absentes / refusées
    console.warn(
      `Impossible de charger les dossards du tournoi ${tournamentId}:`,
      error
    );
  }
  return map;
}

/**
 * Applique les dossards du tournoi sur un joueur (view-model).
 * Ne lit plus `players.dossard` (legacy).
 */
export function applyTournamentDossard<T extends Players>(
  player: T,
  dossards: Map<string, number>
): T {
  const dossard = dossards.get(player.id as string);
  if (typeof dossard === "number") {
    return { ...player, dossard };
  }
  const { dossard: _removed, ...rest } = player as T & { dossard?: number };
  return rest as T;
}

// 🔥 Fonction générique : charge les participations, players et privateData
export async function fetchParticipationsWithPlayers(
  tournamentId: string,
  forceRefresh: boolean = false
): Promise<Participation[]> {
  const db = firestoreInstance;
  if (!tournamentId) {
    throw new Error("Tournament ID is required to load participations.");
  }
  // Charger les participations
  const participationSnap = await getDocs(
    collection(db, "tournois", tournamentId, "participation")
  );
  const privateQueue: { ref: ReturnType<typeof doc>; target: Participation }[] =
    [];
  const playerIdSet = new Set<string>();

  const participationList = participationSnap.docs
    .map((docSnap) => {
      const data = docSnap.data() as ParticipationFirestore;

      // Vérifier que les données essentielles existent
      if (!data || !data.playersId || !data.dateInscription) {
        console.warn(
          `Participation ${docSnap.id} a des données manquantes:`,
          data
        );
        return null;
      }

      const dateInscription = toParticipationDate(data.dateInscription);
      if (!dateInscription) {
        console.warn(
          `Participation ${docSnap.id}: dateInscription invalide`,
          data.dateInscription
        );
        return null;
      }

      const participation = {
        id: docSnap.id,
        ...data,
        dateInscription,
      } as Participation;

      privateQueue.push({
        ref: doc(
          firestoreInstance,
          "tournois",
          tournamentId,
          "participation",
          docSnap.id,
          "private",
          "data"
        ),
        target: participation,
      });
      playerIdSet.add(data.playersId);
      return participation;
    })
    .filter((p): p is Participation => p !== null);

  // Charger les privateData
  const BATCH_SIZE = 100;
  for (let i = 0; i < privateQueue.length; i += BATCH_SIZE) {
    const batch = privateQueue.slice(i, i + BATCH_SIZE);
    await Promise.all(
      batch.map(async ({ ref, target }) => {
        try {
          const snap = await getDoc(ref);
          type PrivateData = {
            email?: string;
            paymentStatus?: string;
          };

          const privateData = snap.exists() ? (snap.data() as PrivateData) : {};
          target.email = privateData.email;
          target.paymentStatus = privateData.paymentStatus as
            | Participation["paymentStatus"]
            | undefined;
        } catch (error) {
          console.warn(
            `Erreur lors du chargement des données privées pour ${target.id}:`,
            error
          );
          target.email = undefined;
          target.paymentStatus = undefined;
        }
      })
    );
  }

  const dossardsMap = await fetchTournamentDossards(tournamentId);
  const playersMap = new Map<string, Players>();
  const playerIds = Array.from(playerIdSet);
  for (let i = 0; i < playerIds.length; i += BATCH_SIZE) {
    const batch = playerIds.slice(i, i + BATCH_SIZE);
    const snaps = await Promise.all(
      batch.map(async (id) => {
        try {
          if (forceRefresh) {
            return await getDocFromServer(doc(db, "players", id));
          }
          return await getDoc(doc(db, "players", id));
        } catch (error) {
          console.warn(`Erreur lors du chargement du joueur ${id}:`, error);
          return null;
        }
      })
    );
    for (const snap of snaps) {
      if (snap && snap.exists()) {
        const playerData = snap.data();
        if (playerData && playerData.name && playerData.surname) {
          const base = { ...playerData, id: snap.id } as Players;
          const { dossard: _legacy, ...withoutLegacy } = base as Players & {
            dossard?: number;
          };
          playersMap.set(
            snap.id,
            applyTournamentDossard(withoutLegacy as Players, dossardsMap)
          );
        } else {
          console.warn(
            `Joueur ${snap.id} a des données incomplètes:`,
            playerData
          );
        }
      }
    }
  }

  const finalParticipations = participationList
    .map((p) => {
      const player = playersMap.get(p.playersId);
      if (!player) {
        console.warn(
          `Joueur non trouvé pour la participation ${p.id}: ${p.playersId}`
        );
        return null;
      }
      return {
        ...p,
        players: player,
      };
    })
    .filter((p): p is Participation => p !== null);

  return finalParticipations;
}
